// src/providers/AuthProvider.jsx
import React, { createContext, useContext, useMemo, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { AuthAPI } from '../api/auth';
import { EnterprisesAPI } from '../api/enterprises';
import { ROUTES } from '../routes';
import {
  clearAuthSessionStorage,
  getAuthSessionFromStorage,
  getLastActivityAt,
  isAuthSessionExpired,
  markUserActivity,
  setAuthSession,
} from '../utils/authSession';

const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;
const ACTIVITY_WRITE_THROTTLE_MS = 15 * 1000;

function getAuthLoginUrl() {
  const envBase = process.env.REACT_APP_AUTH_URL ? process.env.REACT_APP_AUTH_URL.replace(/\/+$/, '') : '';
  const authBase = envBase || (
    typeof window !== 'undefined' && (
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname === '::1'
    )
      ? 'http://localhost:3000'
      : 'https://auth.sushaservices.com'
  );
  return `${authBase}${ROUTES.LOGIN}`;
}

function getAuthLogoutUrl() {
  const envBase = process.env.REACT_APP_AUTH_URL ? process.env.REACT_APP_AUTH_URL.replace(/\/+$/, '') : '';
  const authBase = envBase || (
    typeof window !== 'undefined' && (
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname === '::1'
    )
      ? 'http://localhost:3000'
      : 'https://auth.sushaservices.com'
  );
  return `${authBase}/logout`;
}

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const location = useLocation();
  const [user, setUser] = useState(() => {
    if (isAuthSessionExpired()) {
      clearAuthSessionStorage();
      return null;
    }
    return getAuthSessionFromStorage();
  });

  const setSession = (authPayload) => {
    const nextUser = setAuthSession(authPayload);
    setUser(nextUser);
    const { role, is_demo, full_name, page_access, must_change_password, password_changed } = nextUser;
    return { role, is_demo, full_name, page_access, must_change_password, password_changed };
  };

  React.useEffect(() => {
    if (!user?.is_demo || !user?.session_expires_at) return undefined;

    const expiresAtMs = Date.parse(user.session_expires_at);
    if (Number.isNaN(expiresAtMs)) return undefined;

    const ttlMs = expiresAtMs - Date.now();
      if (ttlMs <= 0) {
        clearAuthSessionStorage();
        setUser(null);
        window.location.href = `${getAuthLogoutUrl()}?expired=session`;
        return undefined;
      }

      const timer = setTimeout(() => {
        clearAuthSessionStorage();
        setUser(null);
        window.location.href = `${getAuthLogoutUrl()}?expired=session`;
      }, ttlMs);

    return () => clearTimeout(timer);
  }, [user]);

  React.useEffect(() => {
    if (!user?.must_change_password) return undefined;
    if (location.pathname.startsWith(ROUTES.CHANGE_PASSWORD)) return undefined;
    if (location.pathname.startsWith(ROUTES.LOGIN)) return undefined;
    window.location.href = `${ROUTES.CHANGE_PASSWORD}?force=1`;
    return undefined;
  }, [location.pathname, user]);

  React.useEffect(() => {
    if (!user || user.is_demo) return undefined;

    let lastWriteMs = 0;
    const writeActivity = () => {
      const now = Date.now();
      if (now - lastWriteMs < ACTIVITY_WRITE_THROTTLE_MS) return;
      lastWriteMs = now;
      const iso = new Date(now).toISOString();
      markUserActivity(iso);
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') writeActivity();
    };

    const events = ['click', 'keydown', 'mousemove', 'scroll', 'touchstart'];
    events.forEach((eventName) => {
      window.addEventListener(eventName, writeActivity, { passive: true });
    });
    document.addEventListener('visibilitychange', onVisibility);

    // Record initial active timestamp when effect starts.
    writeActivity();

    return () => {
      events.forEach((eventName) => {
        window.removeEventListener(eventName, writeActivity);
      });
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [user]);

  React.useEffect(() => {
    if (!user || user.is_demo || user.role === 'super' || user.role === 'superadmin') return undefined;

    let mounted = true;

    const syncSession = async () => {
      try {
        const res = await EnterprisesAPI.getMySession();
        if (!mounted) return;
        const sessionPayload = res?.data?.data || {};
        setUser((current) => {
          if (!current) return current;
          const next = setAuthSession({
            ...current,
            ...sessionPayload,
          });
          return next;
        });
      } catch {
        // ignore transient sync failures; cached session remains usable
      }
    };

    void syncSession();
    const timer = setInterval(syncSession, 60 * 1000);
    const onFocus = () => {
      void syncSession();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void syncSession();
      }
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      mounted = false;
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [user?.id, user?.role, user?.page_access, user?.enterprise_id, user?.enterprise_name, user?.full_name]);

  React.useEffect(() => {
    if (!user || user.is_demo) return undefined;

    const checkInactivity = () => {
      const lastIso = getLastActivityAt() || user.last_activity_at;
      if (!lastIso) return;
      const lastMs = Date.parse(lastIso);
      if (Number.isNaN(lastMs)) return;
      if (Date.now() - lastMs >= INACTIVITY_TIMEOUT_MS) {
        clearAuthSessionStorage();
        setUser(null);
        window.location.href = `${getAuthLogoutUrl()}?expired=inactivity`;
      }
    };

    checkInactivity();
    const timer = setInterval(checkInactivity, 10 * 1000);
    return () => clearInterval(timer);
  }, [user]);

  const value = useMemo(() => ({
    user,
    setUser,
    setSession,
    login: async (email, password) => {
      const res = await AuthAPI.login(email, password);
      return setSession(res?.data || {});
    },
    logout: async () => {
      // purely client-side for now
      clearAuthSessionStorage();
      try {
        Object.keys(localStorage).forEach((k) => {
          if (k.startsWith('susha_chat_')) {
            localStorage.removeItem(k);
          }
        });
      } catch {
        // ignore
      }
      window.location.href = getAuthLogoutUrl();
    },
  }), [user]);

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

const ENTERPRISE_ACCESS_KEYS = new Set([
  'dashboard',
  'assets',
  'monitor',
  'cmdb',
  'reports',
  'network_scan',
  'connectors',
  'maintenance',
  'cloud',
  'role_management',
]);

export function RequireAuth({ children, role, pageKey }) {
  const { user } = useAuth();
  if (!user) {
    window.location.href = getAuthLoginUrl();
    return null;
  }

  const pageAccess = Array.isArray(user.page_access) ? user.page_access : [];
  const hasPageAccess = Boolean(pageKey && ENTERPRISE_ACCESS_KEYS.has(pageKey) && pageAccess.includes(pageKey));
  const isEnterpriseAdminRoute = role === "enterprise_admin";

  const hasRole = user.role === role || (role === 'super' && user.role === 'superadmin');
  if (role && !hasRole) {
    if (!(isEnterpriseAdminRoute && hasPageAccess)) {
      return <Navigate to={['super', 'superadmin'].includes(user.role) ? ROUTES.SUPERADMIN : ROUTES.ENTERPRISE} replace />;
    }
  }

  return children;
}
